import { create } from 'zustand';
import { Broadcast, ScheduleTime } from 'models/broadcast.model';
import { Attachment, BroadcastStatuses } from 'interfaces';
import {
  DraftBroadcastKeys,
  DraftBroadcastValidationField,
  DraftSetOptions,
  BroadcastActions,
  BroadcastState,
  TAB_STATUSES,
} from './broadcast.type';
import {
  validateMessage,
  validateScheduleDate,
  validateScheduleTime,
  validateTitle,
} from './broadcast-validation-helper';

const initialBroadcast: Broadcast = {
  id: '',
  title: '',
  message: '',
  tagIds: [],
  status: BroadcastStatuses.NOT_SAVED,
  schedule: null,
  scheduleTime: { hours: 0, minutes: 0 },
  attachments: [],
  author: null,
  authorId: '',
  createdAt: '',
  updatedAt: '',
};

const initialBroadcastErrors: DraftBroadcastKeys = {
  title: '',
  message: '',
  tagIds: '',
  status: '',
  schedule: '',
  scheduleTime: '',
  attachments: '',
};

const initialState: BroadcastState = {
  inProgressBroadcasts: [],
  publishedBroadcasts: [],
  draft: initialBroadcast,
  errors: initialBroadcastErrors,
  activeTab: TAB_STATUSES.IN_PROGRESS,
};

const updateBroadcastList = (state: BroadcastState) => {
  const { inProgressBroadcasts, draft } = state;
  const index = inProgressBroadcasts.findIndex((br) => br.id === draft.id);

  if (index === -1) {
    return inProgressBroadcasts;
  }

  return inProgressBroadcasts.toSpliced(index, 1, draft);
};

const updateBroadcastField = (
  state: BroadcastState,
  options: DraftSetOptions & {
    field: keyof BroadcastState['draft'];
    value: BroadcastState['draft'][keyof BroadcastState['draft']];
  },
): BroadcastState => {
  const { draft, errors } = state;
  const { field, value, markAsDirty } = options;

  const updatedDraft: BroadcastState['draft'] = {
    ...draft,
    [field]: value,
    ...(markAsDirty ? { status: BroadcastStatuses.NOT_SAVED } : {}),
  };

  const updatedBroadcasts = updateBroadcastList({
    ...state,
    draft: updatedDraft,
  });

  return {
    ...state,
    errors: {
      ...errors,
      [field]: '',
    },
    draft: updatedDraft,
    inProgressBroadcasts: updatedBroadcasts,
  };
};

const deriveScheduleTime = (schedule: Broadcast['schedule']): ScheduleTime => {
  if (!schedule) {
    return { hours: 0, minutes: 0 };
  }

  const date = new Date(schedule);

  return { hours: date.getHours(), minutes: date.getMinutes() };
};

const normalizeBroadcast = (broadcast: Broadcast): Broadcast => ({
  ...broadcast,
  tagIds: broadcast.tagIds ?? [],
  attachments: broadcast.attachments ?? [],
  scheduleTime:
    broadcast.scheduleTime ?? deriveScheduleTime(broadcast.schedule),
});

export const useBroadcastStore = create<BroadcastState & BroadcastActions>(
  (set) => ({
    ...initialState,
    actions: {
      setDraftTitle: (
        title: string,
        { markAsDirty, shouldValidate }: DraftSetOptions = {
          markAsDirty: true,
          shouldValidate: true,
        },
      ) =>
        set((state) => {
          const { draft, errors } = state;

          const updatedDraft: BroadcastState['draft'] = {
            ...draft,
            title: title,
            ...(markAsDirty ? { status: BroadcastStatuses.NOT_SAVED } : {}),
          };

          const updatedBroadcasts = updateBroadcastList({
            ...state,
            draft: updatedDraft,
          });

          const titleError = shouldValidate && validateTitle(title, true);

          if (titleError) {
            return {
              ...state,
              draft: updatedDraft,
              errors: {
                ...errors,
                title: titleError,
              },
              inProgressBroadcasts: updatedBroadcasts,
            };
          }

          return updateBroadcastField(state, {
            field: 'title',
            value: title,
            markAsDirty,
          });
        }),
      setDraftMessage: (
        message: string,
        { markAsDirty, shouldValidate }: DraftSetOptions = {
          markAsDirty: true,
          shouldValidate: true,
        },
      ) =>
        set((state) => {
          const { draft, errors } = state;

          const updatedDraft: BroadcastState['draft'] = {
            ...draft,
            message,
            ...(markAsDirty ? { status: BroadcastStatuses.NOT_SAVED } : {}),
          };

          const updatedBroadcasts = updateBroadcastList({
            ...state,
            draft: updatedDraft,
          });

          const messageError = shouldValidate && validateMessage(message);

          if (messageError) {
            return {
              ...state,
              draft: updatedDraft,
              errors: {
                ...errors,
                message: messageError,
              },
              inProgressBroadcasts: updatedBroadcasts,
            };
          }

          return updateBroadcastField(state, {
            field: 'message',
            value: message,
            markAsDirty,
          });
        }),
      setDraftTagIds: (
        tagIds: string[],
        { markAsDirty }: DraftSetOptions = {
          markAsDirty: true,
        },
      ) =>
        set((state) =>
          updateBroadcastField(state, {
            field: 'tagIds',
            value: tagIds,
            markAsDirty,
          }),
        ),
      setDraftScheduleDate: (
        scheduleDate: string,
        { markAsDirty, shouldValidate }: DraftSetOptions = {
          markAsDirty: true,
          shouldValidate: true,
        },
      ) =>
        set((state) => {
          const { draft, errors } = state;

          const dateError =
            shouldValidate && validateScheduleDate(scheduleDate);

          const getSchedule = (): Date => {
            if (scheduleDate) {
              return new Date(
                `${scheduleDate} ${draft.scheduleTime?.hours ?? 0}:${
                  draft.scheduleTime?.minutes ?? 0
                }`,
              );
            }

            const today = new Date();

            today.setHours(draft.scheduleTime?.hours ?? 0);
            today.setMinutes(draft.scheduleTime?.minutes ?? 0);

            return today;
          };

          const schedule = getSchedule();

          const updatedDraft: BroadcastState['draft'] = {
            ...draft,
            schedule,
            ...(markAsDirty ? { status: BroadcastStatuses.NOT_SAVED } : {}),
          };

          const updatedBroadcasts = updateBroadcastList({
            ...state,
            draft: updatedDraft,
          });

          if (dateError) {
            return {
              ...state,
              draft: updatedDraft,
              errors: {
                ...errors,
                schedule: dateError,
              },
              inProgressBroadcasts: updatedBroadcasts,
            };
          }

          return updateBroadcastField(state, {
            field: 'schedule',
            value: schedule,
            markAsDirty,
          });
        }),
      setDraftScheduleTime: (
        scheduleTime: ScheduleTime,
        { markAsDirty, shouldValidate }: DraftSetOptions = {
          markAsDirty: true,
          shouldValidate: true,
        },
      ) =>
        set((state) => {
          const { draft, errors } = state;

          const updatedDraft: BroadcastState['draft'] = {
            ...draft,
            scheduleTime,
            ...(markAsDirty ? { status: BroadcastStatuses.NOT_SAVED } : {}),
          };

          const updatedBroadcasts = updateBroadcastList({
            ...state,
            draft: updatedDraft,
          });

          const timeError =
            shouldValidate && validateScheduleTime(String(scheduleTime?.hours));

          if (timeError) {
            return {
              ...state,
              draft: updatedDraft,
              errors: {
                ...errors,
                scheduleTime: timeError,
              },
              inProgressBroadcasts: updatedBroadcasts,
            };
          }

          return updateBroadcastField(state, {
            field: 'scheduleTime',
            value: scheduleTime,
            markAsDirty,
          });
        }),
      setDraftPictures: (
        files: Attachment[],
        { markAsDirty }: DraftSetOptions = { markAsDirty: true },
      ) =>
        set((state) => {
          const { draft } = state;

          const updatedDraft: BroadcastState['draft'] = {
            ...draft,
            attachments: files,
            ...(markAsDirty ? { status: BroadcastStatuses.NOT_SAVED } : {}),
          };

          const updatedBroadcasts = updateBroadcastList({
            ...state,
            draft: updatedDraft,
          });

          return {
            ...state,
            draft: updatedDraft,
            inProgressBroadcasts: updatedBroadcasts,
          };
        }),
      setActiveTab: (tab: string) =>
        set((state) => ({
          ...state,
          activeTab: tab,
        })),
    },
  }),
);

export const setDraftBroadcast = (broadcast: Broadcast) => {
  useBroadcastStore.setState({
    draft: normalizeBroadcast(broadcast),
    errors: initialState.errors,
  });
};

export const setBroadcastErrors = (
  newErrors: DraftBroadcastValidationField,
) => {
  const { errors } = useBroadcastStore.getState();

  useBroadcastStore.setState({
    errors: {
      ...errors,
      ...newErrors,
    },
  });
};

export const resetDraft = () => {
  useBroadcastStore.setState({
    draft: initialState.draft,
    errors: initialState.errors,
  });
};

export const setBroadcasts = (broadcasts: Broadcast[]) => {
  const { draft } = useBroadcastStore.getState();
  const [inProgressBroadcasts, publishedBroadcasts] = broadcasts.reduce<
    [Broadcast[], Broadcast[]]
  >(
    ([inProgress, published], item) => {
      if (item.status === BroadcastStatuses.PUBLISHED) {
        published.push(item);
      } else {
        inProgress.push(
          item.id === draft?.id
            ? { ...draft, attachments: item.attachments }
            : item,
        );
      }

      return [inProgress, published];
    },
    [[], []],
  );

  useBroadcastStore.setState({ inProgressBroadcasts, publishedBroadcasts });
};

export const broadcastTemplate = initialBroadcast;
